import { Link, useLocation } from 'react-router-dom'
import { CheckCircle2, Circle, Info } from 'lucide-react'
import { useAuth } from '../../core/auth'
import { useNorseTerm } from './NorseTermData'
import { PHASE_LABEL, progress, stepHref, type CheckItem, type PhaseKey } from './checklist'

/**
 * The checklist, two ways: a card at the top of each NorseTerm phase, and a
 * compact list under NorseTerm in the desktop rail. Same items, same source.
 *
 * Status is never colour alone — a filled check, an empty circle, or an info
 * mark, each with words.
 */

const href = stepHref

function Mark({ done }: { done: boolean | null }) {
  if (done === null) return <Info size={15} className="chk__mark is-info" aria-hidden="true" />
  return done
    ? <CheckCircle2 size={15} className="chk__mark is-done" aria-hidden="true" />
    : <Circle size={15} className="chk__mark" aria-hidden="true" />
}

const srState = (done: boolean | null) => (done === null ? '' : done ? ' (done)' : ' (to do)')

export function ChecklistCard({ phase }: { phase: PhaseKey }) {
  const { checklist } = useNorseTerm()
  const groups = checklist(phase)
  if (!groups.length) return null
  const { done, total } = progress(groups)
  const many = groups.length > 1

  return (
    <section className="card chk" aria-label={`Your checklist, ${PHASE_LABEL[phase]}`}>
      <div className="chk__head">
        <h2 className="chk__title">Your checklist</h2>
        {total > 0 && <span className="chk__count">{done} of {total} done</span>}
      </div>
      {groups.map((g) => (
        <div key={g.role} className="chk__group">
          {many && <div className="opt__label">{g.role}</div>}
          <ul className="chk__list">
            {g.items.map((i) => (
              <li key={g.role + i.id} className={`chk__item${i.done ? ' is-done' : ''}`}>
                <Mark done={i.done} />
                <Link to={href(i)} className="chk__link">
                  {i.label}<span className="visually-hidden">{srState(i.done)}</span>
                </Link>
                {i.detail && <span className="chk__detail">{i.detail}</span>}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  )
}

/** A step, with the hat it is worn under (Teacher, Seminar teacher, Admin…). */
export type Step = CheckItem & { role: string }
export interface StepSection { key: string; title: string; note?: string; items: Step[] }

/**
 * Everyone's steps, in the order the year happens. Before the week: one list,
 * or, for admins, approving proposals then building the schedule. During the week
 * and at the open house, just that. Steps for later in the year are returned
 * separately, so the home page can mention them without listing them.
 */
export function useSteps(): { sections: StepSection[]; later: Step[]; current: Step | undefined } {
  const { checklist, phase } = useNorseTerm()
  const flat = (ph: PhaseKey): Step[] => checklist(ph).flatMap((g) => g.items.map((i) => ({ ...i, role: g.role })))
  // Each kind of work is its own section, so a teacher's course and their
  // seminar never blur together: different jobs, on different timelines.
  const byRole = (items: Step[], when: 'prep' | 'during' | 'show'): StepSection[] => {
    const of = (...roles: string[]) => items.filter((i) => roles.includes(i.role))
    const admin = of('Admin', 'Placement')
    const out: StepSection[] = []
    if (when === 'prep' && admin.some((i) => i.stage === 'schedule') && admin.some((i) => i.stage !== 'schedule')) {
      out.push({ key: 'approve', title: 'Admin · Approve proposals', note: 'Teacher courses, then student projects', items: admin.filter((i) => i.stage !== 'schedule') })
      out.push({ key: 'schedule', title: 'Admin · Build the schedule', note: 'Once courses are approved and course selections are in', items: admin.filter((i) => i.stage === 'schedule') })
    } else {
      out.push({ key: 'admin', title: 'Admin', items: admin })
    }
    out.push({ key: 'office', title: 'Office: attendance and where students are', items: of('Office') })
    out.push({ key: 'teacher', title: when === 'prep' ? 'Teacher · Plan and propose your course'
      : when === 'during' ? 'Teacher · Take attendance' : 'Teacher · Open house', items: of('Teacher') })
    out.push({ key: 'seminar', title: 'Your seminar', items: of('Seminar teacher') })
    out.push({ key: 'visits', title: 'Your site visits', items: of('Site visits') })
    out.push({ key: 'student', title: 'Your steps', items: of('Student') })
    return out
  }
  let sections: StepSection[] = []
  let later: Step[] = []
  if (phase === 'prep') {
    sections = byRole(flat('prep'), 'prep')
    later = [...flat('during'), ...flat('show')]
  } else if (phase === 'during') {
    sections = byRole(flat('during'), 'during')
    later = flat('show')
  } else if (phase === 'show') {
    sections = byRole(flat('show'), 'show')
  }
  sections = sections.filter((x) => x.items.length)
  const current = sections.flatMap((x) => x.items).find((i) => i.done === false)
  return { sections, later, current }
}

/** Links that sit directly under a step, keyed by the step's id. */
const UNDER: Record<string, Array<{ to: string; label: string }>> = {
  project: [{ to: '/norseterm/examples', label: 'Example projects: strong and weak' }],
}

/**
 * Under NorseTerm in the rail: each section of your steps with its own
 * heading and count, then the rosters you keep an eye on. Steps are things
 * to do; rosters are places to look, so they are listed apart.
 */
export function NavChecklist() {
  const { enabled, roles, rosters, phase } = useNorseTerm()
  const { sections, current } = useSteps()
  const { pathname } = useLocation()
  if (!enabled) return null
  const all = sections.flatMap((x) => x.items)
  // Places to look, apart from steps (things to do): the admin's two
  // dashboards, then the lists they manage. A seminar teacher's roster sits
  // in their seminar section, next to the step it belongs to.
  const dashboards = [
    (roles.review || roles.run) && { to: '/norseterm/staff-progress', label: 'Staff: course completion' },
    (roles.review || roles.run) && { to: '/norseterm/student-progress', label: 'Students: course selections and project proposals' },
  ].filter(Boolean) as Array<{ to: string; label: string }>
  const manage = [
    (roles.run || roles.attend) && { to: '/norseterm/roster', label: 'Student list' },
    roles.grant && { to: '/norseterm/staff', label: 'Norse Term staff and roles' },
    roles.run && { to: '/norseterm/credit', label: 'Credit recovery' },
    roles.attend && { to: '/norseterm/attendance', label: 'Attendance' },
    (roles.run || roles.attend || roles.review) && { to: '/norseterm/openhouse', label: 'Open house checklist' },
    (roles.run || roles.attend || roles.review) && { to: '/norseterm/checkins', label: 'Project check-ins' },
  ].filter(Boolean) as Array<{ to: string; label: string }>
  // A teacher's roster from the day placement runs, and attendance always
  // (practice before the week, so it is familiar by Tuesday).
  const rosterCount = rosters.reduce((n, r) => n + r.students.length, 0)
  const extra: Record<string, Array<{ to: string; label: string }>> = {
    seminar: roles.seminar ? [{ to: '/norseterm/seminar', label: 'My seminar students' }] : [],
    teacher: [
      rosters.length > 0 && { to: '/norseterm/myroster', label: `Your roster · ${rosterCount} ${rosterCount === 1 ? 'student' : 'students'}` },
      { to: '/norseterm/attend', label: phase === 'prep' ? 'Take attendance (practice)' : 'Take attendance' },
      { to: '/norseterm/checkins', label: 'Project check-ins' },
    ].filter(Boolean) as Array<{ to: string; label: string }>,
  }
  const links = (title: string, items: Array<{ to: string; label: string }>) => items.length > 0 && (
    <div className="rail__chk" aria-label={title}>
      <div className="rail__chk-head">{title}</div>
      <ul>
        {items.map((r) => (
          <li key={r.to}>
            <Link to={r.to} className={`rail__queue${pathname === r.to ? ' is-here' : ''}`}
              aria-current={pathname === r.to ? 'page' : undefined}><span>{r.label}</span></Link>
          </li>
        ))}
      </ul>
    </div>
  )
  return (
    <>
      {sections.map((sec) => {
        const { done, total } = progress([{ role: '', items: sec.items }])
        return (
          <div key={sec.key} className="rail__chk" aria-label={sec.title}>
            <div className="rail__chk-head"><span>{sec.title}</span>{total > 0 && <span className="rail__chk-count">{done}/{total}</span>}</div>
            <ul>
              {sec.items.map((i) => {
                const to = href(i)
                // Several steps open the same page (My course); only mark the
                // one you are on, or every row lights up.
                const samePage = all.filter((x) => href(x).split('?')[0] === pathname)
                const here = to.split('?')[0] === pathname && (samePage.length === 1 || i === current)
                return (
                  <li key={i.role + i.id}>
                    <Link to={to} className={`rail__chk-item${i.done ? ' is-done' : ''}${i === current ? ' is-current' : ''}${here ? ' is-here' : ''}`}
                      aria-current={here ? 'page' : undefined}>
                      <Mark done={i.done} />
                      <span>{i.label}<span className="visually-hidden">{srState(i.done)}</span></span>
                    </Link>
                    {/* Right under the step it helps with: examples to read before writing a proposal. */}
                    {(UNDER[i.id] ?? []).map((u) => (
                      <Link key={u.to} to={u.to} className={`rail__sub${pathname === u.to ? ' is-here' : ''}`}
                        aria-current={pathname === u.to ? 'page' : undefined}>{u.label}</Link>
                    ))}
                  </li>
                )
              })}
              {(extra[sec.key] ?? []).map((r) => (
                <li key={r.to}>
                  <Link to={r.to} className={`rail__queue${pathname === r.to ? ' is-here' : ''}`}
                    aria-current={pathname === r.to ? 'page' : undefined}><span>{r.label}</span></Link>
                </li>
              ))}
            </ul>
          </div>
        )
      })}
      {links('Admin dashboards', dashboards)}
      {links('Manage', manage)}
    </>
  )
}

/**
 * During the week, what needs doing about attendance today: students absent,
 * courses that have not sent theirs in. Review queues are not here; they are
 * steps, with their counts, so nothing is said twice.
 */
export function WaitingOnYou() {
  const { waiting } = useNorseTerm()
  const items = [
    waiting.absentToday && { n: waiting.absentToday, what: waiting.absentToday === 1 ? 'student absent today' : 'students absent today' },
    waiting.notTakenToday && { n: waiting.notTakenToday, what: waiting.notTakenToday === 1 ? 'course has not taken attendance today' : 'courses have not taken attendance today' },
  ].filter(Boolean) as Array<{ n: number; what: string }>
  if (!items.length) return null
  return (
    <section className="waiting" aria-label="Attendance today">
      <div className="waiting__title">Attendance today</div>
      <ul className="waiting__list">
        {items.map((i) => (
          <li key={i.what}><Link to="/norseterm/attendance" className="chk__link"><span className="work__badge">{i.n}</span> {i.what}</Link></li>
        ))}
      </ul>
    </section>
  )
}

/** The count on the NorseTerm tile and rail link: the same number as the bell. */
export function NorseTermBadge() {
  const { enabled, alerts } = useNorseTerm()
  const n = alerts.reduce((t, a) => t + a.n, 0)
  if (!enabled || !n) return null
  return <span className="work__badge" aria-label={`${n} waiting on you`}>{n}</span>
}

/**
 * The bell in the top bar: what needs *you*, with a number, each line a link
 * to the action. Someone whose course or project proposal was sent back finds
 * out here, without having to think to go and look.
 */
/**
 * The Information page, always one click away for staff: what Norse Term is,
 * what is needed from them, the dates, the example courses.
 */
function NavInfo() {
  const { enabled } = useNorseTerm()
  const { student } = useAuth()
  const { pathname } = useLocation()
  if (!enabled || student) return null
  const here = pathname === '/norseterm/info'
  return (
    <div className="rail__chk">
      <ul>
        <li>
          <Link to="/norseterm/info" className={`rail__queue${here ? ' is-here' : ''}`}
            aria-current={here ? 'page' : undefined}>
            <span>About Norse Term</span>
          </Link>
        </li>
      </ul>
    </div>
  )
}

/** Under NorseTerm in the rail: About Norse Term, then your steps. */
export function NorseTermNav() {
  return (<><NavInfo /><NavChecklist /></>)
}
